import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, Mail, Eye, EyeOff, Sparkles, HeartPulse, HardHat, Bird, Wallet } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Button, Input, Field } from "@/components/premium/UI";
import { Pattern } from "@/components/premium/Patterns";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/" replace />;

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await login(identifier.trim().toLowerCase(), password);
      toast.success("Welcome to Home360");
      nav("/");
    } catch (err) {
      const s = err?.response?.status;
      if (s === 423) toast.error("Account temporarily locked. Try again shortly.");
      else if (s === 401) toast.error("Invalid credentials");
      else toast.error(err?.response?.data?.detail || "Sign in failed");
    } finally { setBusy(false); }
  }

  const modules = [
    { icon: HeartPulse, label: "Health360", color: "text-emerald-300" },
    { icon: Bird, label: "Poultry360", color: "text-amber-300" },
    { icon: HardHat, label: "Projects360", color: "text-orange-300" },
    { icon: Wallet, label: "Finance360", color: "text-blue-300" },
  ];

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="login-page">
      {/* Left hero — always-dark navy command panel */}
      <div className="relative hidden lg:flex lg:w-1/2 overflow-hidden command-centre">
        <Pattern kind="dashboard" className="!opacity-[0.12]" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full text-white">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20 grid place-items-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <div className="font-heading text-2xl font-bold tracking-tight">Home360<span className="text-accent">™</span></div>
              <div className="text-[11px] uppercase tracking-[0.24em] text-white/60">Digital Operations Platform</div>
            </div>
          </div>

          <div className="max-w-md">
            <div className="inline-block eyebrow text-white/70 mb-3">Thom Homestead</div>
            <h1 className="font-heading text-4xl xl:text-5xl font-bold tracking-tight leading-[1.05]">
              One command centre for the entire homestead.
            </h1>
            <p className="mt-5 text-white/80 leading-relaxed">
              Health, poultry, construction, finance, inventory, HR and marketing —
              unified under a single, permission-aware executive workspace.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-3">
              {modules.map(m => (
                <div key={m.label} className="flex items-center gap-2.5 rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 backdrop-blur-sm">
                  <m.icon className={`w-4 h-4 ${m.color}`} />
                  <span className="text-sm font-medium">{m.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="text-[11px] text-white/50">Enterprise-grade RBAC · Offline-ready PWA · Home360™</div>
        </div>
      </div>

      {/* Right — glassmorphism form panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-12 relative overflow-hidden">
        <div className="absolute inset-0 gradient-hero" />
        <Pattern kind="dashboard" />
        <div className="relative z-10 w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-3 justify-center">
            <div className="h-10 w-10 rounded-2xl bg-primary text-primary-foreground grid place-items-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <div className="font-heading font-bold text-xl">Home360<span className="text-accent">™</span></div>
              <div className="eyebrow">Digital Operations Platform</div>
            </div>
          </div>

          <div className="rounded-3xl border border-border/70 bg-card/70 backdrop-blur-2xl shadow-[0_16px_64px_-16px_rgba(11,46,89,0.25)] p-8 animate-fade-up">
            <div className="eyebrow mb-2">Secure sign-in</div>
            <h2 className="font-heading text-3xl font-bold tracking-tight">Welcome back</h2>
            <p className="subtle mt-1.5">Access your Thom Homestead workspace.</p>

            <form onSubmit={submit} className="mt-7 space-y-5" data-testid="login-form">
              <Field label="Username or email">
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    data-testid="login-identifier-input"
                    autoComplete="username"
                    required
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    placeholder="jacob.ziwahwa or you@example.com"
                    className="pl-10 h-11"
                  />
                </div>
              </Field>

              <Field label="Password">
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    data-testid="login-password-input"
                    type={showPw ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••••••••"
                    className="pl-10 pr-10 h-11"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(s => !s)}
                    data-testid="login-toggle-password"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={showPw ? "Hide password" : "Show password"}
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </Field>

              <div className="flex items-center justify-between text-sm">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    data-testid="login-remember"
                    className="rounded border-input"
                  />
                  <span className="text-foreground/80">Remember me</span>
                </label>
                <button
                  type="button"
                  data-testid="login-forgot"
                  className="text-primary hover:underline"
                  onClick={() => toast.info("Please contact your administrator to reset your password.")}
                >
                  Forgot password?
                </button>
              </div>

              <Button type="submit" disabled={busy} size="lg" className="w-full" data-testid="login-submit">
                {busy ? "Signing in…" : "Sign in securely"}
              </Button>
            </form>
          </div>

          <p className="mt-8 text-[11px] text-muted-foreground text-center">
            Home360™ · Digital Homestead Operations Platform
          </p>
        </div>
      </div>
    </div>
  );
}
